import { isEmpty } from 'lodash'
import { useFormikContext } from 'formik'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Checkbox } from 'components/v2/Checkbox'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Label } from 'components/v2/Label'
import { SwitchWithLabel } from 'components/v2/Switch'
import { Row } from 'components/v2/Row'
import { SelectPlaces } from 'features/Places/components/SelectPlaces'
import { IPlace } from 'features/Places/components/SelectPlaces/types'
import { confirmationModalitiesMessages } from 'features/Services/components/ConfirmationModalities/messages'
import { EConfirmationModality } from 'features/Bookings/types'
import { GTCompanyV2Vertical, GTUserSuggestion } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'
import { safeFormatMessage } from 'utils/intl'
import { IVisualizationSettingsCardProps, IVisualizationSettingsForm } from './types'
import { IRouteForm } from 'features/Services/components/NewServicesV2/components/RouteCard/types'
import { MultiUserAutocomplete } from 'features/Users/components/MultiUserAutocomplete'
import { EUserType } from 'features/Users/types'

const CONFIRMATION_MODALITIES = [EConfirmationModality.PAID, EConfirmationModality.RESERVED]

const messages = defineMessages({
  seatsMapLabel: {
    id: 'services.form.seatsMap.label',
    defaultMessage: 'Show seats map'
  },
  petFriendlyLabel: {
    id: 'services.form.petFriendly.label',
    defaultMessage: 'Pet friendly'
  },
  selectPlacesExceptionPlaceholder: {
    id: 'services.form.selectPlacesException.placeholder',
    defaultMessage: 'Select origins to be exempted'
  }
})

const TitleContainer = styled.div<TSFixMe>`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

const ConfirmationModalitiesWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 16px;
  border: 1px solid #eae9e9;
`

const ConfirmationModalityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ConfirmationModalityLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #28265d;
`

const CheckboxesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

export const VisualizationSettingsCard = ({
  salesChannels,
  origins = []
}: IVisualizationSettingsCardProps): ReactElement => {
  const intl = useIntl()
  const { accessToken, isSuperAdmin } = useLogin()
  const { values, setFieldValue, handleBlur, touched, errors } = useFormikContext<
    IVisualizationSettingsForm & IRouteForm
  >()

  useEffect(() => {
    const hasOneConfirmationModalityConfigured = values.confirmationModalities.some(
      ({ salesChannels }) => !isEmpty(salesChannels)
    )
    if (!hasOneConfirmationModalityConfigured) {
      setFieldValue(
        'confirmationModalities',
        values.confirmationModalities.map((confirmationModality) => {
          return { ...confirmationModality, salesChannels }
        })
      )
    }
  }, [])

  useEffect(() => {
    const hasOneConfirmationModalityConfigured = values.confirmationModalities.some(
      ({ salesChannels }) => !isEmpty(salesChannels)
    )
    if (hasOneConfirmationModalityConfigured) {
      setFieldValue(
        'confirmationModalities',
        values.confirmationModalities.map((confirmationModality) => {
          const confirmationModalitiesSalesChannels = confirmationModality.salesChannels.filter(({ id }) =>
            salesChannels.some((salesChannel) => salesChannel.id === id)
          )
          return { ...confirmationModality, salesChannels: confirmationModalitiesSalesChannels }
        })
      )
    }
  }, [salesChannels])

  const isShowingConfirmationModalities = values.vertical !== GTCompanyV2Vertical.Rental

  return (
    <FormLayout.Card>
      <TitleContainer>
        <Heading.H4>
          <FormattedMessage
            id="services.form.section.visualizationSettings.title"
            defaultMessage="Visualization Settings"
          />
        </Heading.H4>
      </TitleContainer>
      <Row noMargin>
        <Column width="50%" noMargin>
          <MultiUserAutocomplete
            id="crewUsers"
            type={EUserType.Crew}
            companyId={isSuperAdmin ? undefined : accessToken?.companyId}
            selectedUsers={values.crewUsers as GTUserSuggestion[]}
            onChange={(newUsers: GTUserSuggestion[]): void => {
              setFieldValue('crewUsers', newUsers)
            }}
            label={<FormattedMessage id="crewUsers.autocomplete.label" defaultMessage="Crew users" />}
            onBlur={handleBlur}
            optional
          />
        </Column>
      </Row>
      {isShowingConfirmationModalities && (
        <>
          <Row noMargin>
            <Column noMargin>
              <Label>
                <FormattedMessage
                  id="services.form.confirmationModalities.label"
                  defaultMessage="Confirmation modalities"
                />
              </Label>
            </Column>
          </Row>
          <Row noMargin>
            <Column noMargin>
              <ConfirmationModalitiesWrapper>
                {CONFIRMATION_MODALITIES.map((confirmationModalityName, index) => {
                  const confirmationModality = values.confirmationModalities.find(
                    ({ name }) => name === confirmationModalityName
                  )

                  const handlePlacesChange = (newPlaces: IPlace[]): void => {
                    if (!confirmationModality) {
                      setFieldValue('confirmationModalities', [
                        ...values.confirmationModalities,
                        {
                          name: confirmationModalityName,
                          salesChannels: [],
                          exceptions: {
                            places: newPlaces
                          }
                        }
                      ])
                    } else {
                      setFieldValue(
                        'confirmationModalities',
                        values.confirmationModalities.map((modality) => {
                          if (modality.name === confirmationModalityName) {
                            return {
                              ...modality,
                              exceptions: {
                                ...modality.exceptions,
                                places: newPlaces
                              }
                            }
                          }
                          return modality
                        })
                      )
                    }
                  }
                  return (
                    <ConfirmationModalityWrapper key={index}>
                      <ConfirmationModalityLabel>
                        {safeFormatMessage({
                          intl,
                          key: confirmationModalityName,
                          messages: confirmationModalitiesMessages
                        })}
                      </ConfirmationModalityLabel>
                      <CheckboxesWrapper>
                        {salesChannels.map((salesChannel) => {
                          const salesChannelIsIncluded = confirmationModality?.salesChannels?.some(
                            ({ id }) => salesChannel.id === id
                          )
                          const handleChange = (): void => {
                            if (salesChannelIsIncluded) {
                              setFieldValue(
                                'confirmationModalities',
                                values.confirmationModalities.map((modality) => {
                                  if (modality.name === confirmationModalityName) {
                                    return {
                                      ...modality,
                                      salesChannels: modality.salesChannels.filter(({ id }) => id !== salesChannel.id)
                                    }
                                  }
                                  return modality
                                })
                              )
                            } else {
                              if (!confirmationModality) {
                                setFieldValue('confirmationModalities', [
                                  ...values.confirmationModalities,
                                  {
                                    name: confirmationModalityName,
                                    salesChannels: [salesChannel],
                                    exceptions: {
                                      places: []
                                    }
                                  }
                                ])
                              } else {
                                setFieldValue(
                                  'confirmationModalities',
                                  values.confirmationModalities.map((modality) => {
                                    if (modality.name === confirmationModalityName) {
                                      return {
                                        ...modality,
                                        salesChannels: [...modality.salesChannels, salesChannel]
                                      }
                                    }
                                    return modality
                                  })
                                )
                              }
                            }
                          }

                          return (
                            <Checkbox
                              id={salesChannel.id}
                              key={salesChannel.id}
                              value={salesChannel.id}
                              checked={salesChannelIsIncluded}
                              onChange={(): void => handleChange()}
                            >
                              {salesChannel.name}
                            </Checkbox>
                          )
                        })}
                      </CheckboxesWrapper>
                      {confirmationModalityName === EConfirmationModality.RESERVED && (
                        <SelectPlaces
                          places={origins}
                          values={confirmationModality?.exceptions.places ?? []}
                          onChange={handlePlacesChange}
                          placeholder={intl.formatMessage(messages.selectPlacesExceptionPlaceholder)}
                        />
                      )}
                    </ConfirmationModalityWrapper>
                  )
                })}
                {touched.confirmationModalities && errors.confirmationModalities && (
                  <ErrorMessage>{errors.confirmationModalities}</ErrorMessage>
                )}
              </ConfirmationModalitiesWrapper>
            </Column>
          </Row>
        </>
      )}
      <Row noMargin>
        <Column width="20%" noMargin>
          <Label>{intl.formatMessage(messages.seatsMapLabel)}</Label>
        </Column>
        <Column noMargin>
          <SwitchWithLabel
            value={values.showSeatsMap}
            onChange={(value): void => {
              setFieldValue('showSeatsMap', value)
            }}
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="20%" noMargin>
          <Label>{intl.formatMessage(messages.petFriendlyLabel)}</Label>
        </Column>
        <Column noMargin>
          <SwitchWithLabel
            value={values.isPetFriendly}
            onChange={(value): void => {
              setFieldValue('isPetFriendly', value)
            }}
          />
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
